"use client";

/**
 * Client-side Sentry SDK init. Called once on mount via useEffect because
 * this codebase is Next 14, which doesn't support `instrumentation-client.ts`
 * (added in Next 15.3). Mount once near the top of the root layout — the
 * effect runs after the initial React render, so errors thrown during that
 * render aren't captured. Acceptable for a primarily-marketing app.
 *
 * Replaces the prior in-house ./components/GlobalErrorHandler.tsx, which is
 * left in place for the 14-day overlap window per the GlitchReplay migration
 * plan.
 */
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { axeIntegration } from "@glitchreplay/a11y";
import { networkProbeIntegration } from "@glitchreplay/network-probe";

const PROJECT_ID = "prj_0c53e164928b0a1f7bd2";
const PUBLIC_KEY = "3f96a35586c8084a81d210d137c318d6";
const DSN = `https://${PUBLIC_KEY}@glitchreplay.com/0`;

// Replay privacy/redaction flags. Static defaults match the dashboard's
// "balanced" level (Settings → Replays → Privacy level on glitchreplay.com).
// Subsequent page loads use whatever the dashboard slider was last set to
// — cached from the previous /config fetch below. rrweb masks at snapshot
// time, so a change in the dashboard takes effect at the *next* page load,
// not mid-session.
const PRIVACY_CACHE_KEY = `gr:privacy:${PROJECT_ID}`;
type PrivacyFlags = {
  maskAllText: boolean;
  maskAllInputs: boolean;
  blockAllMedia: boolean;
};
const DEFAULT_PRIVACY: PrivacyFlags = {
  maskAllText: false,
  maskAllInputs: true,
  blockAllMedia: true,
};
function readCachedPrivacy(): PrivacyFlags {
  if (typeof localStorage === "undefined") return DEFAULT_PRIVACY;
  try {
    const raw = localStorage.getItem(PRIVACY_CACHE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PrivacyFlags>;
      return {
        maskAllText:
          typeof p.maskAllText === "boolean" ? p.maskAllText : DEFAULT_PRIVACY.maskAllText,
        maskAllInputs:
          typeof p.maskAllInputs === "boolean" ? p.maskAllInputs : DEFAULT_PRIVACY.maskAllInputs,
        blockAllMedia:
          typeof p.blockAllMedia === "boolean" ? p.blockAllMedia : DEFAULT_PRIVACY.blockAllMedia,
      };
    }
  } catch {}
  return DEFAULT_PRIVACY;
}

const NOISE_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  "Identifier 'ScreenshotConfig' has already been declared",
  "Failed to connect to MetaMask",
  "Cannot assign to read only property 'pushState' of object",
  "Minified React error #310",
];

const NOISE_REJECTION_MESSAGES_NO_STACK: Array<string | RegExp> = [
  "Failed to fetch",
  "NetworkError when attempting to fetch resource.",
  "Load failed",
  /^Response not (ok|successful)/i,
  "Unhandled promise rejection",
];

const NOISE_DENY_URLS = [
  /zaraz/i,
  /cloudflareinsights/i,
  /connect\.facebook\.net/i,
  /fbevents/i,
  /googletagmanager/i,
  /googlesyndication/i,
  /fundingchoicesmessages/i,
  /google-analytics/i,
  /clarity\.ms/i,
  /js\.hs-(scripts|banner|analytics|adspixel)/i,
  /js\.hscollectedforms/i,
  /js\.hubspot/i,
  /js\.usemessages/i,
  /analytics\.ahrefs/i,
  /apis\.google\.com/i,
  /translate\.googleapis\.com/i,
  /gstatic\.com\/.*\/translate/i,
  /cdn\.jsdelivr\.net/i,
  /gtmpx\.com/i,
  /scriptcdn\.net/i,
  /amazingrefresh\.com/i,
  /img\.icons8\.com/i,
];

type ReplayCfg = {
  replay?: {
    enabled?: boolean;
    sessionSampleRate?: number;
    onErrorSampleRate?: number;
    maskAllText?: boolean;
    maskAllInputs?: boolean;
    blockAllMedia?: boolean;
  };
};

export default function SentryInit() {
  useEffect(() => {
    const PRIVACY = readCachedPrivacy();
    Sentry.init({
      dsn: DSN,
      release:
        process.env.NEXT_PUBLIC_RELEASE ||
        process.env.NEXT_PUBLIC_COMMIT_SHA ||
        "dev",
      // Always-on session replay. See instrumentation-client.ts template
      // for the full rationale; for Next 14 the useEffect already runs
      // after first render so first-frame errors are unrecoverable here,
      // but session mode still gives us viewable replays for everything
      // after init.
      tracesSampleRate: 0,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      denyUrls: NOISE_DENY_URLS,
      integrations: [
        Sentry.replayIntegration(PRIVACY),
        axeIntegration({
          runOn: ["development", "staging", "production"],
          productionSampleRate: 0.05,
          wcagLevel: "AA",
          minImpact: "moderate",
        }),
        networkProbeIntegration({
          verifyResourceErrors: [/imagedelivery\.net/i],
        }),
        // Captures fetch/XHR 5xx responses. The browser auto-logs failed
        // requests to the console, but `fetch()` doesn't reject on HTTP
        // error status, so no exception or unhandled rejection fires —
        // default integrations miss them. Defaults: [[500, 599]], /.*/.
        Sentry.httpClientIntegration(),
      ],
      beforeSend(event, hint) {
        const message =
          event.message ||
          (hint?.originalException as Error | undefined)?.message ||
          "";
        if (NOISE_MESSAGES.some((n) => message.includes(n))) return null;
        const error = hint?.originalException;
        const hasUsableStack = error instanceof Error && !!error.stack;
        if (!hasUsableStack) {
          const matches = NOISE_REJECTION_MESSAGES_NO_STACK.some((p) =>
            typeof p === "string" ? message === p : p.test(message),
          );
          if (matches) return null;
        }
        return event;
      },
    });

    // Dashboard-controlled session sampling. Static init above starts session
    // replay at 1.0; this side-channel lets the dashboard down-sample (or
    // stop) the recorder when a project owner wants to dial back replay
    // volume without redeploying.
    // Session-attempt beacon. Counts every SDK init for the dashboard's
    // errors/session denominator — the /config fetch below decides whether
    // to *persist* this recording, but every attempt should count. sendBeacon
    // survives pagehide so quick-bounce visitors are still tallied, and never
    // delays unload.
    try {
      navigator.sendBeacon(
        `https://glitchreplay.com/api/${PROJECT_ID}/session-attempt?sentry_key=${PUBLIC_KEY}`,
      );
    } catch {}

    fetch(
      `https://glitchreplay.com/api/${PROJECT_ID}/config?sentry_key=${PUBLIC_KEY}`,
      { cache: "no-store" },
    )
      .then(async (r): Promise<ReplayCfg | null> => (r.ok ? ((await r.json()) as ReplayCfg) : null))
      .then((cfg) => {
        // Cache the dashboard's current privacy flags for next page load.
        if (
          cfg?.replay &&
          typeof cfg.replay.maskAllText === "boolean" &&
          typeof cfg.replay.maskAllInputs === "boolean" &&
          typeof cfg.replay.blockAllMedia === "boolean"
        ) {
          try {
            localStorage.setItem(
              PRIVACY_CACHE_KEY,
              JSON.stringify({
                maskAllText: cfg.replay.maskAllText,
                maskAllInputs: cfg.replay.maskAllInputs,
                blockAllMedia: cfg.replay.blockAllMedia,
              }),
            );
          } catch {}
        }

        const replay = Sentry.getReplay();
        if (!replay) return;
        if (!cfg?.replay?.enabled) {
          replay.stop();
          return;
        }
        const sessionRate = Number(cfg.replay.sessionSampleRate ?? 1);
        if (sessionRate < 1 && Math.random() >= sessionRate) {
          replay.stop();
        }
      })
      .catch(() => {
        // Network/CORS/whatever — leave the static 1.0 session recorder running.
      });
  }, []);
  return null;
}
